'use client';

import Link from 'next/link';
import Script from 'next/script';
import { Button } from "@/components/ui/button";
import {
  LineChart, Settings, Laptop,
  Users, Database, Map, ArrowUp
} from 'lucide-react';

const AiReadinessAssessmentClient = () => {
  const benefitCards = [
    { icon: LineChart, title: "Strategic Readiness", description: "How well AI aligns with your business goals and growth plans." },
    { icon: Settings, title: "Process Opportunities", description: "Which workflows are best suited for AI automation." },
    { icon: Laptop, title: "Technology Readiness", description: "Can your current systems support AI implementation." },
    { icon: Users, title: "Team Readiness", description: "Is your team prepared for AI adoption and change." },
    { icon: Database, title: "Data Readiness", description: "Do you have the data quality needed for AI success." },
    { icon: Map, title: "Custom Roadmap", description: "A 90-day implementation plan with priorities and timeline." },
  ];

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      
      <main>
        {/* Hero Section */}
        <section id="hero-section" className="relative overflow-hidden py-20 md:py-24 bg-gray-50 dark:bg-gray-950">
          <div className="absolute inset-0 bg-grid-slate-900/[0.04] bg-[10px_10px] dark:bg-grid-slate-400/[0.05]"></div>
          <div className="container mx-auto px-4 relative">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="text-left">
                <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight">
                  Discover Your Business's
                  <span className="text-blue-600 dark:text-blue-500"> AI Opportunities</span> in 10 Minutes
                </h1>
                <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-xl">
                  Get a personalized AI readiness score and custom implementation roadmap designed specifically for SMBs like yours by completing the form.
                </p>
              </div>
              <div className="h-[480px]">
                <iframe
                    src="https://api.biznovaai.com/widget/form/kpSoPbrfF3ocZr29pOE2"
                    style={{width:'100%',height:'100%',border:'none',borderRadius:'3px'}}
                    id="inline-kpSoPbrfF3ocZr29pOE2" 
                    data-layout="{'id':'INLINE'}"
                    data-trigger-type="alwaysShow"
                    data-trigger-value=""
                    data-activation-type="alwaysActivated"
                    data-activation-value=""
                    data-deactivation-type="neverDeactivate"
                    data-deactivation-value=""
                    data-form-name="AI Assessment"
                    data-height="478"
                    data-layout-iframe-id="inline-kpSoPbrfF3ocZr29pOE2"
                    data-form-id="kpSoPbrfF3ocZr29pOE2"
                    title="AI Assessment"
                >
                </iframe>
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Discover Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-gray-900">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold">What You'll Discover</h2>
                    <p className="text-lg text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">Get personalized insights across 6 key areas of your business.</p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {benefitCards.map((card, index) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-950 rounded-lg p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 dark:border-gray-800">
                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center mb-4">
                                <card.icon className="text-blue-600 dark:text-blue-400 text-2xl" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{card.title}</h3>
                            <p className="text-gray-600 dark:text-gray-400">{card.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Back to Top Section */}
        <section className="py-16 md:py-24 bg-gray-50 dark:bg-gray-950">
            <div className="container mx-auto px-4 text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Ready to Get Started?</h2>
                <Link href="#hero-section" scroll={true}>
                    <Button size="lg" className="bg-blue-600 text-white hover:bg-blue-700 font-semibold py-3 text-lg">
                        <ArrowUp className="mr-2 h-5 w-5" />
                        Get My Free Assessment
                    </Button>
                </Link>
            </div>
        </section>
      </main>
      <Script src="https://api.biznovaai.com/js/form_embed.js" strategy="lazyOnload" />
      <footer className="bg-gray-50 dark:bg-gray-950 py-6">
        <div className="container mx-auto px-4 text-center text-gray-500 dark:text-gray-400">
            <p>&copy; {new Date().getFullYear()} Summit Growth. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

export default AiReadinessAssessmentClient;
